import { CommonModule, isPlatformBrowser } from "@angular/common";
import { HttpErrorResponse } from "@angular/common/http";
import {
  afterNextRender,
  Component,
  inject,
  OnDestroy,
  OnInit,
  PLATFORM_ID,
  signal,
} from "@angular/core";
import { GoogleMapsModule } from "@angular/google-maps";
import { MatDialog } from "@angular/material/dialog";
import { ActivatedRoute, Router } from "@angular/router";
import { Cart } from "@models/cart";
import {
  BehaviorSubject,
  catchError,
  concatMap,
  defer,
  EMPTY,
  filter,
  finalize,
  forkJoin,
  iif,
  map,
  Observable,
  of,
  Subject,
  switchMap,
  take,
  takeUntil,
  tap,
  throwError,
  timer,
} from "rxjs";
import { DateTime } from 'luxon';
import { environment } from "../../../../environments/environment";
import { AppLayoutComponent } from "../../../../shared/components/app-layout/app-layout.component";
import { LoadingComponent } from "../../../../shared/components/loading/loading.component";
import { ModalLoginComponent } from "../../../../shared/components/modal-login/modal-login.component";
import {
  ConfirmActionModalComponent,
  ConfirmDialogData,
} from "../../../shared/modals/confirm-action-modal/confirm-action-modal.component";
import { AuthService } from "../../../services/auth.service";
import { CartService } from "../../../services/cart.service";
import { EventsManagementService } from "../../../services/events-management.service";
import { GoogleMapsLoaderService } from "../../../services/google-maps-loader.service";
import { GoogleMapsService } from "../../../services/google-maps.service";
import { SnackBarService } from "../../../services/snack-bar.service";
import { StarredEventsService } from "../../../services/starred-events.service";
import { EventComboTicket } from "@models/combo-ticket";
import { EventSpace } from "@models/space";
import {
  Batch,
  CurrentBatch,
  EventInfo,
  Ticket,
} from "../../../services/types/events-management-request.type";
import { ticketShouldAppearOnEventPage } from "../../../shared/helpers/ticket-availability/ticket-availability.helper";
import { TicketTypeEnum } from "@models/enums/ticket-type.enum";
import { EventDetailsComponent } from "./event-details/event-details.component";
import { AppContinuation, getAppContinuation } from "../../../shared/helpers/deferred-deep-link/deferred-deep-link.helper";
import { AttributionContextService } from "../../../services/marketing/attribution-context.service";
import { MarketingPixelService } from "../../../services/marketing/marketing-pixel.service";
import {
  MarketingCommerceItem,
  MarketingJourneyContext,
} from "../../../services/marketing/marketing.models";
import { EventSeoService } from "../../../services/marketing/seo/event-seo.service";
import { PendingCouponService } from "../../../services/pending-coupon.service";

@Component({
  selector: "app-event-details-container",
  imports: [
    CommonModule,
    GoogleMapsModule,
    AppLayoutComponent,
    EventDetailsComponent,
    LoadingComponent,
  ],
  templateUrl: "./event-details-container.component.html",
  styleUrl: "./event-details-container.component.scss",
})
export class EventDetailsContainerComponent implements OnInit, OnDestroy {
  private readonly eventsManagementService = inject(EventsManagementService);
  private readonly mapsLoader = inject(GoogleMapsLoaderService);
  private readonly googleMapService = inject(GoogleMapsService);
  private readonly starredEventsService = inject(StarredEventsService);
  private readonly cartService = inject(CartService);
  private readonly authService = inject(AuthService);
  private readonly snackbarService = inject(SnackBarService);
  private readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);
  private readonly dialog = inject(MatDialog);
  private readonly platformId = inject(PLATFORM_ID);
  private readonly marketingContext = inject(AttributionContextService);
  private readonly marketingPixels = inject(MarketingPixelService);
  private readonly eventSeo = inject(EventSeoService);
  private readonly pendingCoupon = inject(PendingCouponService);

  constructor() {
    // Maps/geocoding are browser-only; public event HTML must also render for crawlers.
    afterNextRender(() => {
      void this.loadGoogleMaps().catch(() => undefined);
      this.getEventMapLocation();
      this.watchOnlineCutoff();
    });
  }

  isLoading = signal(false);
  isBatchLoading = signal(true);
  appContinuation = signal<AppContinuation | null>(null);
  /** Evento gratuito-privado sem acesso (backend retornou 403). */
  inviteOnly = signal(false);
  /** Cupom de ?cupom= gravado para o checkout (ex.: link de comissário). */
  pendingCouponCode = signal<string | null>(null);

  private currentEventId: string | null = null;
  private destroy$ = new Subject<void>();

  get isUserLoggedIn(): boolean {
    return !!this.authService.getLocalStorageUserInfo();
  }

  private readonly _eventInfoSubject = new BehaviorSubject<EventInfo | null>(
    null,
  );
  readonly eventInfo$ = this._eventInfoSubject.asObservable();
  currentBatch$?: Observable<Batch | null>;
  eventMapCenterLocation$?: Observable<google.maps.LatLngLiteral>;

  readonly spaceIdsInCart = signal<Map<number, number>>(new Map());
  readonly loadingSpaceIds = signal<Set<number>>(new Set());
  readonly isCheckoutInProgress = signal(false);
  private lastCart?: Cart;

  ngOnInit(): void {
    this.subscribeToEventId();
  }

  ngOnDestroy(): void {
    this.marketingPixels.leaveEvent();
    this.marketingContext.leaveEvent();
    this.destroy$.next();
    this.destroy$.complete();
  }

  private watchOnlineCutoff(): void {
    this.eventInfo$.pipe(
      switchMap(info => {
        if (!info?.server_time || info.online_sales_closed) return EMPTY;
        const remaining = DateTime.fromISO(info.start_date).toMillis() - DateTime.fromISO(info.server_time).toMillis();
        if (!Number.isFinite(remaining)) return EMPTY;
        // Browser timeout bound, not a business-rule fallback or device clock.
        return timer(Math.max(0, Math.min(remaining, 2_147_483_647))).pipe(
          switchMap(() => this.eventsManagementService.getEventInfo(info.id)),
          tap(updated => {
            if (this._eventInfoSubject.value?.id === info.id) this._eventInfoSubject.next(updated);
          }),
          catchError(() => EMPTY),
        );
      }),
      takeUntil(this.destroy$),
    ).subscribe();
  }

  handleFavoriteToggle() {
    this.eventInfo$
      .pipe(
        take(1),
        filter((info) => !!info),
        concatMap((info) =>
          this.starredEventsService.updateStarredEvent(info!.id).pipe(
            tap(() => {
              this._eventInfoSubject.next({
                ...info!,
                is_favorite: !info!.is_favorite,
              });
            }),
          ),
        ),
      )
      .subscribe();
  }

  handleAddSpaceClick(space: EventSpace) {
    if (!space.ticket_id) {
      console.error("Space without ticket_id", space);
      return;
    }
    const ticketId = Number(space.ticket_id);

    const eventId = this._eventInfoSubject.value?.id;
    if (!eventId) return;

    this.setSpaceLoading(ticketId, true);
    this.addWithEventConflictGuard(
      (replace) =>
        this.cartService.addItem({ ticket_id: ticketId, quantity: 1 }, replace),
      this.marketingCartContext([
        {
          id: ticketId,
          title: space.title,
          price: space.price,
          addToCartQuantity: 1,
        } as Ticket,
      ]),
    )
      .pipe(
        switchMap((added) =>
          added
            ? forkJoin({
                cart: this.cartService.getCart(),
                eventInfo: this.eventsManagementService.getEventInfo(eventId),
              })
            : of(undefined),
        ),
        tap((result) => {
          if (!result) return;
          this.updateSpaceIdsInCart(result.cart);
          this.lastCart = result.cart;
          this._eventInfoSubject.next(
            this.withComboQuantities(result.eventInfo, result.cart),
          );
          this.snackbarService.openSnackBar(
            "Espaço adicionado ao carrinho!",
            "Fechar",
            "success",
          );
        }),
        catchError((error) => {
          console.error("Erro ao adicionar espaço no carrinho:", error);
          this.snackbarService.openSnackBar(
            "Erro ao adicionar espaço ao carrinho.",
            "Fechar",
            "error",
          );
          return of(undefined);
        }),
        finalize(() => this.setSpaceLoading(ticketId, false)),
      )
      .subscribe();
  }

  handleRemoveSpaceClick(space: EventSpace) {
    const ticketId = Number(space.ticket_id);

    const cartItemId = this.spaceIdsInCart().get(ticketId);
    if (!cartItemId) {
      return;
    }
    const eventId = this._eventInfoSubject.value?.id;
    if (!eventId) return;

    this.setSpaceLoading(ticketId, true);
    this.cartService
      .removeItem(cartItemId)
      .pipe(
        switchMap(() =>
          forkJoin({
            cart: this.cartService.getCart(),
            eventInfo: this.eventsManagementService.getEventInfo(eventId),
          }),
        ),
        catchError((error) => {
          console.error("Erro ao remover espaço do carrinho:", error);
          this.snackbarService.openSnackBar(
            "Erro ao remover espaço do carrinho.",
            "Fechar",
            "error",
          );
          return of(undefined);
        }),
        finalize(() => this.setSpaceLoading(ticketId, false)),
      )
      .subscribe((result) => {
        if (!result) return;
        this.updateSpaceIdsInCart(result.cart);
        this.lastCart = result.cart;
        this._eventInfoSubject.next(
          this.withComboQuantities(result.eventInfo, result.cart),
        );
        this.snackbarService.openSnackBar(
          "Espaço removido do carrinho!",
          "Fechar",
          "success",
        );
      });
  }

  private setSpaceLoading(ticketId: number, loading: boolean) {
    this.loadingSpaceIds.update((ids) => {
      const next = new Set(ids);
      if (loading) {
        next.add(ticketId);
      } else {
        next.delete(ticketId);
      }
      return next;
    });
  }

  handleCheckoutClick(ticketsToBuy: Ticket[]) {
    if (this.isCheckoutInProgress()) return;
    this.isCheckoutInProgress.set(true);

    if (ticketsToBuy.length === 0) {
      this.router.navigate(["/cart"]).finally(() => {
        this.isCheckoutInProgress.set(false);
      });
      return;
    }

    this.addWithEventConflictGuard(
      (replace) => this.addTickets(ticketsToBuy, replace),
      this.marketingCartContext(ticketsToBuy),
    )
      .pipe(
        tap((added) => {
          if (added) this.router.navigate(["/cart"]);
        }),
        catchError((error) => this.handleAddTicketsError(error)),
        finalize(() => this.isCheckoutInProgress.set(false)),
      )
      .subscribe();
  }

  /**
   * Adiciona os ingressos ao carrinho. Com `replace`, esvazia o carrinho no PRIMEIRO add e
   * adiciona os demais em seguida (sequencial, para não correr com a limpeza); sem replace,
   * adiciona todos em paralelo.
   */
  private addTickets(tickets: Ticket[], replace: boolean): Observable<unknown> {
    const toDTO = (ticket: Ticket) => ({
      ticket_id: Number(ticket.id),
      quantity: Number(ticket.addToCartQuantity),
    });

    if (!replace) {
      return forkJoin(
        tickets.map((ticket) => this.cartService.addItem(toDTO(ticket))),
      );
    }

    const [first, ...rest] = tickets;
    return this.cartService
      .addItem(toDTO(first), true)
      .pipe(
        switchMap(() =>
          rest.length
            ? forkJoin(
                rest.map((ticket) => this.cartService.addItem(toDTO(ticket))),
              )
            : of(null),
        ),
      );
  }

  /**
   * Executa uma operação de adicionar ao carrinho; se o backend recusar por conflito de
   * evento (409 `different_event_in_cart`), abre o modal de confirmação e, ao confirmar,
   * refaz a operação com `replace = true` (esvaziando o carrinho). Emite `true` se adicionou,
   * `false` se o usuário cancelou. Demais erros são repassados.
   */
  private addWithEventConflictGuard(
    run: (replace: boolean) => Observable<unknown>,
    context?: MarketingJourneyContext,
  ): Observable<boolean> {
    // Keep the action's original destination even if its request finishes after navigation.
    const eventId = Number(this.currentEventId);
    return run(false).pipe(
      map(() => true),
      catchError((error) => {
        if (!this.isDifferentEventError(error)) return throwError(() => error);

        return this.openReplaceCartDialog().pipe(
          switchMap((confirmed) =>
            confirmed ? run(true).pipe(map(() => true)) : of(false),
          ),
        );
      }),
      tap((added) => {
        if (added && eventId)
          this.marketingPixels.journey(eventId, "cart_item_added", context);
      }),
    );
  }

  private marketingCartContext(tickets: Ticket[]): MarketingJourneyContext {
    const info = this._eventInfoSubject.value;
    const category = info?.event_category?.title;
    const items: MarketingCommerceItem[] = tickets.flatMap((ticket) => {
      // The public combo selector emits only its ticket ID and commercial quantity.
      const combo = info?.combo_tickets?.find(
        (candidate) => Number(candidate.ticket_id) === Number(ticket.id),
      );
      const source = combo ?? ticket;
      const price = source.price == null ? NaN : Number(source.price);
      const quantity = Number(ticket.addToCartQuantity);
      const unitPriceCents = Math.round(price * 100);
      if (
        !Number.isSafeInteger(Number(ticket.id)) ||
        Number(ticket.id) <= 0 ||
        !Number.isSafeInteger(quantity) ||
        quantity <= 0 ||
        !Number.isSafeInteger(unitPriceCents) ||
        unitPriceCents < 0
      )
        return [];
      const batch = combo?.source_batch_number ?? ticket.batchNumber;
      return [
        {
          ticket_id: Number(ticket.id),
          quantity,
          unit_price_cents: unitPriceCents,
          ...(source.title ? { name: source.title } : {}),
          ...(category ? { category } : {}),
          ...(batch != null ? { variant: `Lote ${batch}` } : {}),
        },
      ];
    });
    return {
      ...this.marketingEventContext(info),
      currency: "BRL",
      // Do not label a partial known subtotal as the entire add action's value.
      ...(items.length === tickets.length
        ? {
            items,
            value_cents: items.reduce(
              (sum, item) => sum + item.unit_price_cents * item.quantity,
              0,
            ),
          }
        : {}),
    };
  }

  private marketingEventContext(
    info: EventInfo | null,
  ): MarketingJourneyContext {
    return {
      ...(info?.title ? { event_name: info.title } : {}),
      ...(info?.event_category?.title
        ? { event_category: info.event_category.title }
        : {}),
    };
  }

  private openReplaceCartDialog(): Observable<boolean> {
    return this.dialog
      .open(ConfirmActionModalComponent, {
        width: "440px",
        maxWidth: "95vw",
        maxHeight: "90dvh",
        data: {
          modalTitle: "Trocar de evento?",
          modalContent:
            "Seu carrinho já tem ingressos de outro evento. Deseja esvaziar o carrinho e adicionar este(s) ingresso(s)?",
          confirmMessage: "Esvaziar e adicionar",
          cancelMessage: "Cancelar",
        } as ConfirmDialogData,
      })
      .afterClosed()
      .pipe(
        take(1),
        map((confirmed) => !!confirmed),
      );
  }

  private isDifferentEventError(error: unknown): boolean {
    return (
      error instanceof HttpErrorResponse &&
      error.status === 409 &&
      error.error?.code === "different_event_in_cart"
    );
  }

  private handleAddTicketsError(error: unknown): Observable<boolean> {
    console.error("Erro ao adicionar produto no carrinho:", error);
    this.snackbarService.openSnackBar(
      this.soldOutAddToCartMessage(error),
      "Fechar",
      "error",
    );
    return of(false);
  }

  private soldOutAddToCartMessage(error: unknown): string {
    if (
      error instanceof HttpErrorResponse &&
      error.status === 422 &&
      String(error.error?.error ?? "").includes("Insufficient tickets")
    ) {
      return "Este ingresso está esgotado.";
    }

    return "Erro ao adicionar ingressos ao carrinho.";
  }

  private subscribeToEventId() {
    this.route.paramMap
      .pipe(
        map((m) => m.get("id")),
        takeUntil(this.destroy$),
      )
      .subscribe((eventId) => {
        if (eventId) {
          this.currentEventId = eventId;
          this.updateAppContinuation(eventId);
          this.eventSeo.clear();
          this.capturePendingCoupon(eventId);
          this.requestEventInfo(eventId);
          this.requestCurrentBatch(eventId);
        }
      });
  }

  private updateAppContinuation(eventId: string): void {
    if (!isPlatformBrowser(this.platformId)) {
      this.appContinuation.set(null);
      return;
    }
    this.appContinuation.set(getAppContinuation(eventId, navigator.userAgent, navigator.platform, navigator.maxTouchPoints));
  }

  /** Grava ?cupom= na sessão/localStorage para o checkout aplicar ao entrar no carrinho. */
  private capturePendingCoupon(eventId: string): void {
    const code = this.route.snapshot.queryParamMap.get("cupom");
    const captured = this.pendingCoupon.captureFromQuery(eventId, code);
    if (captured) {
      this.pendingCouponCode.set(captured.code);
      this.snackbarService.openSnackBar(
        `Cupom ${captured.code} registrado — será vinculado no checkout.`,
        "Fechar",
        "success",
      );
      void this.router.navigate([], {
        relativeTo: this.route,
        queryParams: { cupom: null },
        queryParamsHandling: "merge",
        replaceUrl: true,
      });
      return;
    }

    this.pendingCouponCode.set(
      this.pendingCoupon.getForEvent(eventId)?.code ?? null,
    );
  }

  private requestEventInfo(eventId: string) {
    this.isLoading.set(true);
    this.inviteOnly.set(false);

    this.eventsManagementService
      .getEventInfo(Number(eventId))
      .pipe(
        take(1),
        catchError((error: HttpErrorResponse) => {
          if (eventId !== this.currentEventId) return of(null);
          this.eventSeo.clear();
          // 403 público restante: gratuito-privado sem convite (rascunho/cancelado também).
          if (error.status === 403) {
            this.inviteOnly.set(true);
          } else {
            this.snackbarService.openSnackBar(
              "Não foi possível carregar o evento.",
              "Fechar",
              "error",
            );
          }
          return of(null);
        }),
        finalize(() => this.isLoading.set(false)),
      )
      .subscribe((info) => {
        // A slow previous-event response must never replace the current canonical/preview.
        if (eventId !== this.currentEventId) return;
        if (info) {
          this.eventSeo.setEvent(info);
          this._eventInfoSubject.next(
            this.withComboQuantities(info, this.lastCart),
          );
          this.marketingContext.eventLoaded(Number(eventId));
          this.marketingPixels.eventLoaded(
            Number(eventId),
            this.marketingEventContext(info),
          );
        }
      });
  }

  openLogin(): void {
    const dialogRef = this.dialog.open(ModalLoginComponent, {
      width: "500px",
      maxWidth: "95vw",
      maxHeight: "90dvh",
    });

    dialogRef
      .afterClosed()
      .pipe(take(1))
      .subscribe((userLogged) => {
        if (userLogged && this.currentEventId) {
          this.requestEventInfo(this.currentEventId);
          this.requestCurrentBatch(this.currentEventId);
        }
      });
  }

  private requestCurrentBatch(eventId: string) {
    const cart$ = defer(() => {
      const user = this.authService.getLocalStorageUserInfo(); // sync
      return iif(
        () => !!user,
        this.cartService.getCart().pipe(
          take(1),
          tap((cart) => {
            this.lastCart = cart;
            this.updateSpaceIdsInCart(cart);
            const info = this._eventInfoSubject.value;
            if (info) {
              this._eventInfoSubject.next(this.withComboQuantities(info, cart));
            }
          }),
          catchError(() => of(undefined)), // se falhar, segue o fluxo
        ),
        of(undefined),
      );
    });

    this.isBatchLoading.set(true);
    this.currentBatch$ = cart$.pipe(
      switchMap((cart: Cart | undefined) =>
        this.eventsManagementService.getEventCurrentBatch(Number(eventId)).pipe(
          map((batch: CurrentBatch) =>
            batch.pdv_only ? null : this.mapBatchTickets(batch, cart),
          ),
          catchError(() => of(null)),
        ),
      ),
      finalize(() => this.isBatchLoading.set(false)),
    ) as Observable<Batch | null>;
  }

  private withComboQuantities(info: EventInfo, cart?: Cart): EventInfo {
    const items = cart?.cart_items ?? [];
    const comboTickets: EventComboTicket[] = (info.combo_tickets ?? []).map(
      (combo) => ({
        ...combo,
        addToCartQuantity:
          items.find((item) => item.ticket_id === Number(combo.ticket_id))
            ?.quantity ??
          combo.addToCartQuantity ??
          0,
      }),
    );
    return { ...info, combo_tickets: comboTickets };
  }

  private updateSpaceIdsInCart(cart?: Cart): void {
    if (!cart) return;
    const spaceIds = new Map<number, number>(
      cart.cart_items
        .filter((item) => item.ticket_type === "SpaceTicket")
        .map((item) => [item.ticket_id, item.id]),
    );
    this.spaceIdsInCart.set(spaceIds);
  }

  private mapBatchTickets(batch: CurrentBatch, cart?: Cart) {
    const tickets = batch.tickets
      .map((ticket) => {
        const cartTicket = cart?.cart_items.find(
          (item) => item.ticket_id === ticket.id,
        );

        const mappedTicket = {
          ...ticket,
          addToCartQuantity: cartTicket?.quantity ?? 0,
          price: Number(ticket.price),
          // Catálogo pode misturar lotes (meia no 2, inteira no 1) — usa o lote do ingresso.
          batchNumber: ticket.batch_number ?? batch.batch_number,
        };
        return mappedTicket;
      })
      .filter((ticket) => ticketShouldAppearOnEventPage(ticket))
      .filter((ticket) => ticket.availability === "general public")
      .filter(
        (ticket) =>
          ticket.type !== TicketTypeEnum.SpaceTicket &&
          ticket.type !== TicketTypeEnum.ComboTicket,
      )
      .sort((a, b) => {
        const aGroup = a.parent_ticket_id ?? a.id;
        const bGroup = b.parent_ticket_id ?? b.id;
        if (aGroup !== bGroup) return aGroup - bGroup;
        if (!!a.half_price !== !!b.half_price) return a.half_price ? 1 : -1;

        const diff = a.price - b.price;
        if (diff !== 0) return diff;

        const an = (a.title ?? "").toString();
        const bn = (b.title ?? "").toString();
        return an.localeCompare(bn);
      });
    return {
      ...batch,
      tickets: tickets,
    };
  }

  private async loadGoogleMaps() {
    const apiKey = environment.GOOGLE_API_KEY_MAPS;

    await this.mapsLoader.load(apiKey);
  }

  private getEventMapLocation() {
    this.eventMapCenterLocation$ = this.eventInfo$?.pipe(
      filter((eventInfo) => !!eventInfo),
      switchMap((eventInfo) => {
        const mapsUrl = eventInfo!.address.maps_url;
        if (mapsUrl) {
          return of(this.getMapLocationFromUrl(mapsUrl));
        } else {
          const address = this.formatAddressMaps(eventInfo!);

          return this.googleMapService
            .getGeoCode(address, eventInfo!.address.country)
            .pipe(
              map((getCodeResponse) => {
                const results = getCodeResponse.results;
                let center: google.maps.LatLngLiteral = {
                  lat: 0,
                  lng: 0,
                };

                if (results.length > 0) {
                  const location = results[0]?.geometry?.location;
                  if (location) {
                    center = { lat: location.lat, lng: location.lng };
                  }
                }
                return center;
              }),
            );
        }
      }),
    );
  }

  private getMapLocationFromUrl(mapsUrl: string) {
    let center: google.maps.LatLngLiteral = {
      lat: 0,
      lng: 0,
    };

    const url = new URL(mapsUrl);
    const params = new URLSearchParams(url.search);
    const query = params.get("query");
    if (query) {
      const [lat, lng] = query.split(",").map(Number);
      center = { lat, lng };
    }

    return center;
  }

  private formatAddressMaps(eventInfo: EventInfo): string {
    const {
      location_name,
      street,
      number,
      city_name,
      state,
      postal_code,
      country,
    } = eventInfo.address;

    const addressParts = [
      location_name,
      street,
      number,
      city_name,
      state,
      postal_code,
      country,
    ].map((part) => String(part || "").trim());

    return addressParts.filter(Boolean).join(" ");
  }
}
